import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import MasterTransfertoDistributorTable from "./MasterTransfertoDistributorTable";

const MasterTransfertoDistributor = () => {
  const mid = useSelector(
    (s) => s.auth.employeeDetails?.username
  );

  const mobile_number = useSelector(
    (state) => state.auth.employeeDetails?.mobile_number
  );

  const { callApi: hierarchy } = useApi(
    "/hierarchy/root",
    "GET"
  );

  const { callApi: transferApi } = useApi(
    "/p2p/transfer",
    "POST"
  );

  const { callApi: reversalApiCall } = useApi(
    "/p2p/reversal",
    "POST"
  );



  const [merchants, setMerchants] = useState([]);
  const [loadingMerchants, setLoadingMerchants] =
    useState(false);

  const [merchantSearch, setMerchantSearch] =
    useState("");
  const [selectedMerchant, setSelectedMerchant] =
    useState(null);
  const [showDropdown, setShowDropdown] =
    useState(false);

  const [amount, setAmount] = useState("");
  const [loadingTransfer, setLoadingTransfer] =
    useState(false);

  const [message, setMessage] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [refreshLedgerKey, setRefreshLedgerKey] = useState(0);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [mpinModal, setMpinModal] = useState({ show: false, mpin: "", pendingPayload: null, type: null });
  const mpinRefs = useRef([]);

  /* ===== Transfer Type State ===== */
  const [transferType, setTransferType] =
    useState("TRANSFER"); // default selected

  /* ================= FETCH MERCHANTS ================= */
  const fetchMerchants = async () => {
    setLoadingMerchants(true);
    try {
      const res = await hierarchy(
        null,
        `?mobile_number=${mobile_number}`
      );

      const merchantList =
        res?.self?.children?.filter(
          (c) => c.role === "DISTRIBUTOR"
        ) || [];

      setMerchants(merchantList);
    } catch (err) {
      setMerchants([]);
      setMessage({
        type: "error",
        text: "Failed to load merchants",
      });
    } finally {
      setLoadingMerchants(false);
    }
  };

  const handleMerchantFocus = () => {
    setShowDropdown(true);
    if (merchants.length === 0) {
      fetchMerchants();
    }
  };

  const filteredMerchants = merchants.filter((m) =>
    `${m.name} ${m.mobile}`
      .toLowerCase()
      .includes(merchantSearch.toLowerCase())
  );

  const handleMerchantSelect = (m) => {
    setSelectedMerchant(m);
    setMerchantSearch(`${m.name} (${m.mobile})`);
    setShowDropdown(false);
  };

  /* ================= SEND / REVERSAL ================= */
  const handleSend = () => {
    setMessage(null);

    if (!selectedMerchant) {
      setMessage({ type: "error", text: "Please select a merchant" });
      return;
    }

    if (!amount || Number(amount) <= 0) {
      setMessage({ type: "error", text: "Please enter valid amount" });
      return;
    }

    const payload = transferType === "TRANSFER"
      ? { senderMid: mid, receiverMid: selectedMerchant.mid, amount: Number(amount), currency: "INR", walletType: "MRCHNT", remarks: "P2P Send" }
      : { requesterMid: mid, targetMid: selectedMerchant.mid, amount: Number(amount), currency: "INR", walletType: "MRCHNT", remarks: "Distributor balance adjustment" };

    setMpinModal({ show: true, mpin: "", pendingPayload: payload, type: transferType === "TRANSFER" ? "transfer" : "reversal" });
  };

  const handleMpinConfirm = async () => {
    if (mpinModal.mpin.length !== 6) return;

    setLoadingTransfer(true);

    try {
      const apiCall = mpinModal.type === "reversal" ? reversalApiCall : transferApi;
      const res = await apiCall({ ...mpinModal.pendingPayload, mpin: mpinModal.mpin });
      setMpinModal({ show: false, mpin: "", pendingPayload: null, type: null });

      if (res?.status === "SUCCESS" || res?.status === "success") {
        setSuccessData(res);
        setAmount("");
        setSelectedMerchant(null);
        setMerchantSearch("");
      } else {
        setMessage({ type: "error", text: res?.message || "Operation failed" });
      }
    } catch (err) {
      setMpinModal({ show: false, mpin: "", pendingPayload: null, type: null });
      setMessage({
        type: "error",
        text: err?.response?.data?.message || err?.response?.data?.error || err?.message || "Operation failed. Please try again",
      });
    } finally {
      setShowDropdown(false);
      setLoadingTransfer(false);
    }
  };


  const handleClearSearch = () => {
    setMerchantSearch("");
    setSelectedMerchant(null);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="min-h-screen md:min-h-screen bg-white md:bg-gradient-to-br md:from-indigo-50 md:via-white md:to-slate-100 md:py-6 px-4 md:px-4 overflow-x-hidden">
      <div className={`max-w-6xl mx-auto space-y-6 h-[100vh] md:h-auto ${!showHistory ? "" : ""}`}>
        <div className="bg-white md:rounded-[32px] md:shadow-2xl p-4 md:p-8">
          <div className="space-y-6">

            {/* Title */}
            <div className="text-center">
              <h1 className="text-2xl md:text-xl font-bold text-gray-800">
                Send Money
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Send funds instantly to Distributor
              </p>
            </div>

            {/* Merchant Input */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Select Distributor
              </label>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Distributor..."
                  value={merchantSearch}
                  onChange={(e) => setMerchantSearch(e.target.value)}
                  onFocus={handleMerchantFocus}
                  className="w-full border-2 border-gray-200 rounded-2xl px-5 py-5 md:py-4 pr-12 text-xl md:text-lg focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none transition"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                  🔍
                </span>

                {/* CLEAR BUTTON */}
                {merchantSearch && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 text-xl font-bold"
                  >
                    ✕
                  </button>
                )}
              </div>

              {showDropdown && (
                <div className="absolute left-0 right-0 bg-white border rounded-2xl shadow-xl max-h-52 overflow-auto z-20 mt-2">
                  {loadingMerchants ? (
                    <div className="p-4 text-sm text-gray-500">
                      Loading...
                    </div>
                  ) : filteredMerchants.length === 0 ? (
                    <div className="p-4 text-sm text-gray-400">
                      No merchants found
                    </div>
                  ) : (
                    filteredMerchants.map((m) => (
                      <button
                        key={m.mid}
                        onClick={() => handleMerchantSelect(m)}
                        className="w-full text-left px-5 py-4 hover:bg-indigo-50 transition"
                      >
                        <div className="font-semibold text-gray-800 text-base">
                          {m.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {m.mobile}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Enter Amount
              </label>

              <input
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setAmount(value);
                  }
                }}
                className="w-full border-2 border-gray-200 rounded-2xl px-5 py-4 text-xl font-semibold focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none transition"
              />
            </div>

            {selectedMerchant && (
              <div className="text-center text-lg font-semibold text-green-600">
                Available Balance: ₹
                {selectedMerchant?.balances?.mainBalance}
              </div>
            )}

            {/* Transfer Type Dropdown */}
            <div className="pt-2 relative">
              <label className="block text-sm font-medium text-gray-600 mb-2">
                Transfer Type
              </label>

              {/* Trigger */}
              <button
                type="button"
                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                className="w-full border-2 border-gray-200 rounded-2xl px-5 py-4 text-lg font-semibold bg-white flex justify-between items-center focus:ring-2 focus:ring-indigo-400"
              >
                {transferType === "TRANSFER" ? "Forward" : "Reverse"}
                <span className="text-gray-400">▼</span>
              </button>

              {/* Dropdown */}
              {showTypeDropdown && (
                <div className="absolute left-0 right-0 mt-2 bg-white border rounded-2xl shadow-xl z-20">
                  <button
                    onClick={() => {
                      setTransferType("TRANSFER");
                      setShowTypeDropdown(false);
                    }}
                    className="w-full text-left px-5 py-4 hover:bg-indigo-50"
                  >
                    Forward
                  </button>

                  <button
                    onClick={() => {
                      setTransferType("REVERSAL");
                      setShowTypeDropdown(false);
                    }}
                    className="w-full text-left px-5 py-4 hover:bg-indigo-50"
                  >
                    Reverse
                  </button>
                </div>
              )}
            </div>
            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={loadingTransfer}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5 md:py-4 rounded-2xl text-xl md:text-lg font-bold transition disabled:opacity-50 md:shadow-lg"
            >
              {loadingTransfer
                ? "Processing..."
                : "Send Now"}
            </button>

            {/* Message */}
            {message && (
              <div
                className={`p-3 rounded-2xl text-sm font-medium text-center ${message.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
                  }`}
              >
                {message.text}
              </div>
            )}
          </div>
          {/* Desktop History */}
          <div className="hidden md:block rounded-3xl p-5">
            <MasterTransfertoDistributorTable
              bid={mid}
              refreshKey={refreshLedgerKey}
              selectedMerchant={selectedMerchant}
            />
          </div>

          {/* Mobile History Button */}
          <div className="md:hidden pt-4">
            <button
              onClick={() => setShowHistory(true)}
              className="w-full border border-indigo-600 text-indigo-600 py-3 rounded-2xl font-semibold"
            >
              View History
            </button>
          </div>

          {/* FULL SCREEN MOBILE HISTORY */}
          {showHistory && (
            <div className="fixed inset-0 z-50 bg-white md:hidden flex flex-col">

              {/* HEADER */}
              <div className="flex items-center justify-between px-4 py-4 border-b">
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-indigo-600 font-semibold"
                >
                  ← Back
                </button>

                <h2 className="text-lg font-bold text-gray-800">
                  Transaction History
                </h2>

                <div className="w-10" />
              </div>

              {/* BODY */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <MasterTransfertoDistributorTable
                  bid={mid}
                  refreshKey={refreshLedgerKey}
                  selectedMerchant={selectedMerchant}
                />
              </div>

            </div>
          )}
        </div>
      </div>
      {/* SUCCESS POPUP */}
      {successData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4">

          <div className="bg-white rounded-3xl w-full max-w-md p-6 space-y-4 shadow-2xl animate-fadeIn">

            <h2 className="text-xl font-bold text-green-600 text-center">
              Transaction Successful
            </h2>

            <div className="space-y-2 text-sm text-gray-700">

              <div className="flex justify-between">
                <span className="font-medium">Transaction ID:</span>
                <span className="font-mono text-xs break-all">
                  {successData.txnId}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="font-medium">Sender Balance:</span>
                <span>₹ {successData.senderBalance}</span>
              </div>

              <div className="flex justify-between">
                <span className="font-medium">Receiver Balance:</span>
                <span>₹ {successData.receiverBalance}</span>
              </div>

              <div className="text-center pt-2 font-semibold text-gray-800">
                {successData.message}
              </div>

            </div>

            <button
              onClick={() => {
                setSuccessData(null);
                setRefreshLedgerKey(prev => prev + 1);
              }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-2xl font-semibold transition"
            >
              OK
            </button>

          </div>
        </div>
      )}

      {mpinModal.show && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 space-y-5 shadow-2xl">
            <h2 className="text-xl font-bold text-gray-800 text-center">Enter MPIN</h2>
            <p className="text-sm text-gray-500 text-center">
              Enter your 6-digit MPIN to confirm the transaction.
            </p>
            <div className="flex justify-center gap-2">
              {[...Array(6)].map((_, i) => (
                <input
                  key={i}
                  ref={(el) => (mpinRefs.current[i] = el)}
                  type="password"
                  maxLength={1}
                  value={mpinModal.mpin[i] || ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    const arr = mpinModal.mpin.split("");
                    arr[i] = val;
                    setMpinModal(prev => ({ ...prev, mpin: arr.join("") }));
                    if (val && i < 5) mpinRefs.current[i + 1]?.focus();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !mpinModal.mpin[i] && i > 0)
                      mpinRefs.current[i - 1]?.focus();
                  }}
                  className="w-11 h-11 text-center text-xl border-2 border-slate-300 rounded-lg focus:border-indigo-600 outline-none"
                />
              ))}
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => { setMpinModal({ show: false, mpin: "", pendingPayload: null, type: null }); mpinRefs.current = []; }}
                className="flex-1 border-2 border-gray-300 text-gray-600 py-3 rounded-2xl font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleMpinConfirm}
                disabled={mpinModal.mpin.length !== 6 || loadingTransfer}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-2xl font-semibold disabled:opacity-50"
              >
                {loadingTransfer ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default MasterTransfertoDistributor;

